import {
  normalizeCallerNumber,
  staffReservationRequestSchema,
  type ReservationStatus,
  type StaffReservationRequest
} from "@glowin/schemas";

/**
 * Logic for the Reservations tab, kept out of the component so it can be tested directly.
 *
 * Dates here are the RESTAURANT'S dates. Someone checking tonight's bookings from another timezone still means
 * the restaurant's tonight, so "today" comes from the restaurant's timezone, never the browser's.
 */

/** Today's date at the restaurant, YYYY-MM-DD. */
export function restaurantToday(timeZone: string | undefined, now = new Date()): string {
  return new Intl.DateTimeFormat("en-CA", {
    timeZone: timeZone || undefined,
    year: "numeric",
    month: "2-digit",
    day: "2-digit"
  }).format(now);
}

/** A date plus or minus whole days. Calendar arithmetic, so no timezone can move it. */
export function shiftDate(date: string, days: number): string {
  const [y, m, d] = date.split("-").map(Number);
  return new Date(Date.UTC(y!, m! - 1, d! + days)).toISOString().slice(0, 10);
}

/** "Today", "Tomorrow", or "Sat, Oct 3". */
export function dayHeading(date: string, today: string): string {
  if (date === today) return "Today";
  if (date === shiftDate(today, 1)) return "Tomorrow";
  if (date === shiftDate(today, -1)) return "Yesterday";
  const [y, m, d] = date.split("-").map(Number);
  return new Date(Date.UTC(y!, m! - 1, d!)).toLocaleDateString("en-US", {
    weekday: "short",
    month: "short",
    day: "numeric",
    timeZone: "UTC"
  });
}

/** "19:30" → "7:30 PM". */
export function clockLabel(time: string): string {
  const [h, m] = time.split(":").map(Number);
  const suffix = h! < 12 ? "AM" : "PM";
  const h12 = h! % 12 === 0 ? 12 : h! % 12;
  return `${h12}:${String(m).padStart(2, "0")} ${suffix}`;
}

const STATUS_LABELS: Record<ReservationStatus, string> = {
  CONFIRMED: "Booked",
  SEATED: "Seated",
  NO_SHOW: "No-show",
  CANCELLED: "Cancelled"
};

export function statusLabel(s: ReservationStatus): string {
  return STATUS_LABELS[s] ?? s;
}

export function statusTone(s: ReservationStatus): "good" | "neutral" | "bad" {
  if (s === "SEATED") return "good";
  if (s === "NO_SHOW") return "bad";
  return "neutral";
}

export interface StatusAction {
  to: ReservationStatus;
  label: string;
  /** Cancelling frees the table for good, so it asks first. */
  confirm?: boolean;
}

/**
 * What staff can do from a status. A no-show or a seated party can be put back to booked, because the commonest
 * mistake is the wrong button. A cancellation is final; the server refuses to undo one, because the seats have
 * already gone back to the phone line.
 */
export function actionsFor(s: ReservationStatus): StatusAction[] {
  switch (s) {
    case "CONFIRMED":
      return [
        { to: "SEATED", label: "Seated" },
        { to: "NO_SHOW", label: "No-show" },
        { to: "CANCELLED", label: "Cancel", confirm: true }
      ];
    case "SEATED":
    case "NO_SHOW":
      return [{ to: "CONFIRMED", label: "Undo" }];
    default:
      return [];
  }
}

/** "+16265081917" → "(626) 508-1917"; anything else as it is. */
export function phoneLabel(e164: string): string {
  const m = /^\+1(\d{3})(\d{3})(\d{4})$/.exec(e164);
  return m ? `(${m[1]}) ${m[2]}-${m[3]}` : e164;
}

export interface BookingForm {
  date: string;
  time: string;
  partySize: string;
  name: string;
  phone: string;
  notes: string;
}

export type FormResult =
  | { ok: true; request: StaffReservationRequest }
  | { ok: false; errors: Partial<Record<keyof BookingForm, string>> };

/**
 * The form, checked in the same words the person would use. The number is accepted the way people type it —
 * (626) 508-1917 — and stored in E.164, so the phone agent can find the booking when that person calls.
 */
export function readBookingForm(f: BookingForm): FormResult {
  const errors: Partial<Record<keyof BookingForm, string>> = {};
  const party = Number(f.partySize);
  if (!f.date) errors.date = "Choose a date.";
  if (!f.time) errors.time = "Choose a time.";
  if (!Number.isInteger(party) || party < 1) errors.partySize = "Enter how many people.";
  else if (party > 500) errors.partySize = "That is more than one booking can hold.";
  if (!f.name.trim()) errors.name = "Enter a name.";
  let phoneE164: string | undefined;
  if (f.phone.trim()) {
    phoneE164 = normalizeCallerNumber(f.phone);
    if (!phoneE164) errors.phone = "Enter a phone number, e.g. (626) 508-1917.";
  }
  if (Object.keys(errors).length > 0) return { ok: false, errors };

  const parsed = staffReservationRequestSchema.safeParse({
    date: f.date,
    time: f.time,
    partySize: party,
    name: f.name.trim(),
    ...(phoneE164 ? { phoneE164 } : {}),
    ...(f.notes.trim() ? { notes: f.notes.trim() } : {})
  });
  if (!parsed.success) return { ok: false, errors: { name: parsed.error.issues[0]?.message ?? "Check the details." } };
  return { ok: true, request: parsed.data };
}
